import React, { useRef } from "react";
import { faFileUpload } from "@fortawesome/free-solid-svg-icons";
import { parsedFieldData } from "../../../types/PepTypes";
import Loading from "../../../ui-elements/Loading/Loading";
import FileUpload from "../PepFileUpload";
import { PepWatchUploadDataTable } from "./PepWatchUploadDataTable";
import Link from "../../../ui-elements/Link/Link";
import Button from "../../../ui-elements/Button/Button";
import ProgressBar from "../../../ui-elements/ProgressBar/ProgressBar";
import { ValidationSummary } from "../PepWatchUploadSummarySection";

// Enhanced interface with better typing and new props
interface FileUploadSectionProps {
  isFileUploadOpen: boolean;
  setIsFileUploadOpen: (open: boolean) => void;
  uploadedFile: File | null;
  isLoading: boolean;
  validData: any[];
  invalidData: any[];
  validationProgress: { current: number; total: number } | null;
  isValidating: boolean;
  isSubmittingBulk?: boolean; // Prop for bulk submission state
  onFileUpload: (file: File) => void;
  onResetFile: () => void;
  onStopValidation: () => void;
  onBulkSubmit: (data: parsedFieldData[]) => void;
  // Optional props for enhanced functionality
  validationStats?: {
    sessionId: string;
    estimatedTimeRemaining: number | null;
    processingRate: number | null;
  };
  isHidden?: boolean;
}

export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  isFileUploadOpen,
  setIsFileUploadOpen,
  uploadedFile,
  isLoading,
  validData,
  invalidData,
  validationProgress,
  isValidating,
  isSubmittingBulk = false, // Default to false for backward compatibility
  onFileUpload,
  onResetFile,
  onStopValidation,
  onBulkSubmit,
  validationStats,
  isHidden = false, // Default to false for backward compatibility
}) => {
  const fileUploadRef = useRef<{ triggerFileInput: () => void, clearFileInput: () => void }>(null);

  // Don't render anything if component is hidden
  if (isHidden) {
    return null;
  }

  // Enhanced file upload click handler with better error handling
  const handleFileUploadClick = () => {
    try {
      setIsFileUploadOpen(true);
      setTimeout(() => {
        fileUploadRef.current?.triggerFileInput();
      }, 100);
    } catch (error) {
      console.error("Error opening file upload:", error);
    }
  };

  // Enhanced bulk submit handler with validation
  const handleBulkSubmit = () => {
    if (validData.length === 0) {
      console.warn("No valid data to submit");
      return;
    }

    if (isSubmittingBulk) {
      console.warn("Bulk submission already in progress");
      return;
    }

    onBulkSubmit(validData);
  };

  // Calculate if submission should be disabled
  const isSubmissionDisabled = invalidData.length > 0 || validData.length === 0 || isSubmittingBulk || isValidating;

  return (
    <div className="fileUpload__body">
      {/* File upload instructions and template link */}
      <div className="fileUpload__instructions">
        <span style={{ marginRight: "5px" }} className="paragraph">
          Hægt er að bæta við einstaklingum úr Excel skjali, sjá
        </span>
        <Link
          url="https://cdn.livemarketdata.com/keldan/PEP_voktun_einstaklingar_snidmat.xlsx"
          targetBlank
          icon="excel"
          style={{ marginRight: "30px" }}
          linkSize="18"
        >
          Sniðmát í Excel
        </Link>
      </div>

      {/* Upload button - disabled during validation or submission */}
      <Button
        icon={faFileUpload}
        buttonType="secondary"
        onClick={handleFileUploadClick}
        disabled={isValidating || isSubmittingBulk}
      >
        Hlaða upp skjali
      </Button>
      <p className="paragraph--bold" style={{ width: "100%" }}>
        Verð eru samkvæmt{" "}
        <Link linkSize="18" url="/Verdskra#Gagnaleit">
          verðskrá Keldunnar
        </Link>{" "}
        eða samningi hverju sinni.
      </p>

      {/* File upload modal component */}
      <FileUpload
        ref={fileUploadRef}
        isOpen={isFileUploadOpen}
        onClose={() => setIsFileUploadOpen(false)}
        onFileUpload={onFileUpload}
      />

      {/* File processing and results section */}
      {uploadedFile && (
        <div style={{ marginTop: 15, width: '100%' }}>
          {/* Show progress bar during validation */}
          {isValidating && validationProgress && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 8 }}>
                Að staðfesta gögn...
              </div>
              <ProgressBar
                current={validationProgress.current}
                total={validationProgress.total}
                showPercentage={true}
                showCount={true}
                size="medium"
                // Pass validation session ID if available for better reset detection
                validationKey={validationStats?.sessionId}
              />

              {/* Enhanced progress information */}
              {validationStats && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  {validationStats.estimatedTimeRemaining && (
                    <div>
                      Áætlaður tími: {Math.ceil(validationStats.estimatedTimeRemaining / 1000)} sekúndur
                    </div>
                  )}
                  {validationStats.processingRate && (
                    <div>
                      Hraði: {validationStats.processingRate.toFixed(1)} færslur/sek
                    </div>
                  )}
                </div>
              )}

              {/* Stop validation button */}
              <div style={{ marginTop: 10 }}>
                <Button 
                  buttonType="secondary" 
                  size="sm" 
                  onClick={onStopValidation} 
                  disabled={isSubmittingBulk}
                >
                  Stöðva
                </Button>
              </div>
            </div>
          )}

          {/* Show progress bar during bulk submission */}
          {isSubmittingBulk && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 8 }}>
                Að senda gögn...
              </div>
              <Loading />
              <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                Að senda {validData.length} færslur
              </div>
            </div>
          )}

          {/* Show loading spinner for initial file parsing */}
          {isLoading && !isValidating && !isSubmittingBulk ? (
            <div>
              <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 8 }}>
                Að lesa skjal...
              </div>
              <Loading />
            </div>
          ) : (
            // Show results after validation is complete and not submitting
            !isValidating &&
            !isSubmittingBulk && (
              <>
                {/* Show invalid data table if there are errors, otherwise show valid data */}
                {invalidData.length > 0 ? (
                  <PepWatchUploadDataTable data={invalidData} isInvalidData={true} />
                ) : (
                  // Show valid data table when there are no errors
                  validData.length > 0 && (
                      <PepWatchUploadDataTable data={validData} isInvalidData={false} />
                  )
                )}

                {/* Summary information with field errors */}
                <ValidationSummary
                  validCount={validData.length}
                  invalidCount={invalidData.length}
                  invalidData={invalidData}
                />

                {/* Action buttons */}
                <div style={{ display: "flex", gap: "10px", marginTop: 15 }}>
                  <Button 
                    buttonType="secondary" 
                    size="lg" 
                    onClick={() => { 
                      fileUploadRef.current?.clearFileInput(); 
                      onResetFile(); 
                    }} 
                    disabled={isSubmittingBulk}
                  >
                    Eyða skjali
                  </Button>
                  <Button
                    onClick={handleBulkSubmit}
                    disabled={isSubmissionDisabled}
                    buttonType="primary"
                    size="lg"
                  >
                    {isSubmissionDisabled 
                      ? 'Senda' 
                      : isSubmittingBulk 
                        ? `Sendi ${validData.length} færslur...` 
                        : `Senda ${validData.length} færslur`
                    }
                  </Button>
                </div>

                {/* Help text for disabled submission */}
                {isSubmissionDisabled && !isSubmittingBulk && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginTop: 8,
                    }}
                  >
                    {invalidData.length > 0
                      ? "Lagaðu villurnar til að geta sent gögnin"
                      : validData.length === 0
                      ? "Engar gildar færslur til að senda"
                      : "Ekki hægt að senda á meðan staðfesting er í gangi"}
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};